import { createGlobalStyle } from 'styled-components';
import { normalize } from 'styled-normalize';

import LazzerBoldOtf from './assets/fonts/Lazzer/Lazzer-Bold.otf';
import LazzerBoldTtf from './assets/fonts/Lazzer/Lazzer-Bold.ttf';

import LazzerHeavyOtf from './assets/fonts/Lazzer/Lazzer-Heavy.otf';
import LazzerHeavyTtf from './assets/fonts/Lazzer/Lazzer-Heavy.ttf';

import LazzerLightOtf from './assets/fonts/Lazzer/Lazzer-Light.otf';
import LazzerLightTtf from './assets/fonts/Lazzer/Lazzer-Light.ttf';

import LazzerMediumOtf from './assets/fonts/Lazzer/Lazzer-Medium.otf';
import LazzerMediumTtf from './assets/fonts/Lazzer/Lazzer-Medium.ttf';

import LazzerRegularOtf from './assets/fonts/Lazzer/Lazzer-Regular.otf';
import LazzerRegularTtf from './assets/fonts/Lazzer/Lazzer-Regular.ttf';

import LazzerSemiBoldOtf from './assets/fonts/Lazzer/Lazzer-SemiBold.otf';
import LazzerSemiBoldTtf from './assets/fonts/Lazzer/Lazzer-SemiBold.ttf';

import LazzerThinOtf from './assets/fonts/Lazzer/Lazzer-Thin.otf';
import LazzerThinTtf from './assets/fonts/Lazzer/Lazzer-Thin.ttf';

import GeistMonoMediumOtf from './assets/fonts/geist_mono/GeistMono-Medium.otf';
import GeistMonoMediumWoff2 from './assets/fonts/geist_mono/GeistMono-Medium.woff2';

import GeistMonoRegularOtf from './assets/fonts/geist_mono/GeistMono-Regular.otf';
import GeistMonoRegularWoff2 from './assets/fonts/geist_mono/GeistMono-Regular.woff2';

const AppStyle = createGlobalStyle`
  ${normalize}

  // Fonts

  @font-face {
    font-family: 'Lazzer';
    font-weight: 200;
    src: url(${LazzerThinTtf}) format('truetype'),
        url(${LazzerThinOtf}) format('opentype');
  }

  @font-face {
    font-family: 'Lazzer';
    font-weight: 300;
    src: url(${LazzerLightTtf}) format('truetype'),
        url(${LazzerLightOtf}) format('opentype');
  }

  @font-face {
    font-family: 'Lazzer';
    font-weight: 400;
    src: url(${LazzerRegularOtf}) format('truetype'),
        url(${LazzerRegularTtf}) format('opentype');
  }

  @font-face {
    font-family: 'Lazzer';
    font-weight: 500;
    src: url(${LazzerMediumTtf}) format('truetype'),
        url(${LazzerMediumOtf}) format('opentype');
  }

  @font-face {
    font-family: 'Lazzer';
    font-weight: 600;
    src: url(${LazzerSemiBoldOtf}) format('truetype'),
        url(${LazzerSemiBoldTtf}) format('opentype');
  }

  @font-face {
    font-family: 'Lazzer';
    font-weight: 700;
    src: url(${LazzerBoldOtf}) format('truetype'),
        url(${LazzerBoldTtf}) format('opentype');
  }


  @font-face {
    font-family: 'Lazzer';
    font-weight: 800;
    src: url(${LazzerHeavyOtf}) format('truetype'),
        url(${LazzerHeavyTtf}) format('opentype');
  }

  @font-face {
    font-family: 'Geist Mono';
    font-weight: 400;
    src: url(${GeistMonoRegularWoff2}) format('woff2'),
        url(${GeistMonoRegularOtf}) format('opentype');
  }

  @font-face {
    font-family: 'Geist Mono';
    font-weight: 500;
    src: url(${GeistMonoMediumWoff2}) format('woff2'),
        url(${GeistMonoMediumOtf}) format('opentype');
  }


  // Variables
  
  :root {
    // Spectrum Colors

    --orange-100: rgba(235,111,21,1.00);
    --orange-70: rgba(235,111,21,0.70);
    --orange-40: rgba(235,111,21,0.40);
    --orange-20: rgba(235,111,21,0.20);

    --brown-100: rgba(136,80,44,1.00);
    --brown-70: rgba(136,80,44,0.70);
    --brown-40: rgba(136,80,44,0.40);
    --brown-20: rgba(136,80,44,0.20);

    --cream-100: rgba(207,174,145,1.00);
    --cream-70: rgba(207,174,145,0.70);
    --cream-40: rgba(207,174,145,0.40);
    --cream-20: rgba(207,174,145,0.20);

    --lavender-100: rgba(104,98,118,1.00);
    --lavender-70: rgba(104,98,118,0.70);
    --lavender-40: rgba(104,98,118,0.40);
    --lavender-20: rgba(104,98,118,0.20);

    --purple-100: rgba(44,31,74,1.00);
    --purple-70: rgba(44,31,74,0.70);
    --purple-40: rgba(44,31,74,0.40);
    --purple-20: rgba(44,31,74,0.20);

    --yellow-100: rgba(251,227,142,1.00);
    --yellow-70: rgba(251,227,142,0.70);
    --yellow-40: rgba(251,227,142,0.40);
    --yellow-20: rgba(251,227,142,0.20);

    --gray-100: rgba(20,20,20,1.00);
    --gray-700: rgba(79,77,68,1.00);
    --gray-40: rgba(102,99,94,1.00);
    --gray-20: rgba(102,99,94,0.20);
    --gray-8: rgba(79,77,68,0.08);
    --gray-6: rgba(79,77,68,0.06);
    --gray-4: rgba(79,77,68,0.04);
    --gray-4-solid: #F8F6F3; // Special color to match gray-4 over elevation-mid

    --white: rgba(255,255,255,1.00);
    --black: rgba(0, 0, 0, 1.00);

    // Semantic Colors

    --primary-default: var(--orange-100);
    --primary-hover: rgba(239,121,35,1.00);
    --primary-pressed: rgba(231,109,21,1.00);

    --secondary-default: var(--gray-700);
    --secondary-hover: var(--gray-100);
    --secondary-pressed: var(--gray-100);

    --success-default: rgba(52, 199, 88, 1);

    --critical-default: rgba(234,67,53,1.00);
    --critical-hover: rgba(240,76,63,1.00);
    --critical-pressed: rgba(225,59,45,1.00);

    --wash-orange-default: rgba(229,96,0,0.05);
    --wash-orange-hover: rgba(229,96,0,0.10);
    --wash-orange-pressed: rgba(229,96,0,0.15);

    --wash-gray-default: var(--gray-4);
    --wash-gray-hover: var(--gray-6);
    --wash-gray-pressed: var(--gray-8);

    --wash-red-default: rgba(234,67,53,0.05);
    --wash-red-hover: rgba(234,67,53,0.10);
    --wash-red-pressed: rgba(234,67,53,0.15);

    --content-title: var(--gray-100);
    --content-body: var(--gray-700);
    --content-subtitle: rgba(59, 58, 57, 0.50);
    --content-muted: rgba(0, 0, 0, 0.50);

    --border-default: rgba(0,0,0,0.20);
    --border-muted: rgba(0,0,0,0.10);
    --border-light: rgba(0,0,0,0.05);

    --elevation-low: rgba(250,247,240,1.00);
    --elevation-mid: rgba(255,253,250,1.00);
    --elevation-high: var(--white);
    --elevation-indented: rgba(240,237,230,0.50);

    --cream-hover: var(--cream-20);
    --brown-default: var(--brown-100);
    --brown-hover: var(--brown-70);

    --action-tertiary-default: var(--content-subtitle);
    --action-tertiary-hover: #4F4D44;

    --link-default: #0000EE;

    // Position
    --z-dropdown: 10;
    --z-modal: 20;
    --z-notification: 30;
    --z-tooltip: 40;

    // Sizing
    --border-width: 1px;

    // Fonts
    --font-family-title: 'Lazzer', sans-serif;
    --font-family-body: 'Inter', sans-serif;
    --font-family-mono: 'Geist Mono', monospace;

  }

  a {
    color: var(--primary-default);
    text-decoration: underline;
  }

  html {
    height: 100%;
    background: var(--elevation-mid);
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
  }

  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }


  .hidden {
    z-index: -1;
    display: none;
  }

  #typeahead-menu {
 top: initial!important;
 left: initial!important;
}
`;

export { AppStyle };
